import { apiRequest, ApiError } from '@/lib/api/client';
import { getAuthToken } from '@/lib/api/token-store';

function createAuthHeaders(token, headers = {}) {
  const normalizedToken = String(token || getAuthToken() || '').trim();
  if (!normalizedToken) {
    throw new ApiError('authorization token is required', 401);
  }
  return {
    Authorization: `Bearer ${normalizedToken}`,
    ...headers,
  };
}

function toOptionalTrimmedString(value) {
  const normalized = String(value || '').trim();
  return normalized || '';
}

function toRfc3339(value, fieldName) {
  if (!value) {
    throw new ApiError(`${fieldName} is required`, 400);
  }

  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) {
      throw new ApiError(`${fieldName} is required`, 400);
    }
    const parsed = new Date(trimmed);
    if (Number.isNaN(parsed.getTime())) {
      throw new ApiError(`${fieldName} must be a valid RFC3339 datetime`, 400);
    }
    return parsed.toISOString();
  }

  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) {
      throw new ApiError(`${fieldName} must be a valid RFC3339 datetime`, 400);
    }
    return value.toISOString();
  }

  throw new ApiError(`${fieldName} must be a string or Date`, 400);
}

function normalizeIdList(values) {
  if (!values) {
    return [];
  }
  const source = Array.isArray(values) ? values : [values];
  return source
    .map((value) => String(value || '').trim())
    .filter(Boolean);
}

/** Flattens arrays, splits comma-separated tokens, dedupes (slots API multi-value formats). */
function normalizeMultiValueIds(...sources) {
  const out = [];
  for (const values of sources) {
    if (values === undefined || values === null) {
      continue;
    }
    const source = Array.isArray(values) ? values : [values];
    for (const item of source) {
      const str = String(item ?? '').trim();
      if (!str) {
        continue;
      }
      for (const part of str.split(',')) {
        const t = part.trim();
        if (t) {
          out.push(t);
        }
      }
    }
  }
  return [...new Set(out)];
}

function normalizeTenantIds({ tenantId, tenantIds } = {}) {
  return normalizeMultiValueIds(tenantIds, tenantId);
}

function normalizeSlotCalendarIds({ calendarId, calendarIds } = {}) {
  return normalizeMultiValueIds(calendarIds, calendarId);
}

function toOptionalRfc3339(value, fieldName) {
  if (value === undefined || value === null) {
    return '';
  }
  if (typeof value === 'string' && !value.trim()) {
    return '';
  }
  return toRfc3339(value, fieldName);
}

function isIncludeManualFlag(value) {
  if (value === true) {
    return true;
  }
  if (typeof value === 'string') {
    const v = value.trim().toLowerCase();
    return v === 'true' || v === '1' || v === 'yes';
  }
  return false;
}

function clampLimit(value, fallback = 500) {
  if (value === undefined || value === null || value === '') {
    return fallback;
  }
  const n = Number(value);
  if (!Number.isFinite(n)) {
    return fallback;
  }
  return Math.min(2000, Math.max(1, Math.trunc(n)));
}

function toNonNegativeInt(value, fallback = 0) {
  if (value === undefined || value === null || value === '') {
    return fallback;
  }
  const n = Number(value);
  if (!Number.isFinite(n) || n < 0) {
    return fallback;
  }
  return Math.trunc(n);
}

export async function fetchCalendars({ token, q, doctorId } = {}) {
  const query = new URLSearchParams();
  const normalizedQ = toOptionalTrimmedString(q);
  const normalizedDoctorId = toOptionalTrimmedString(doctorId);

  if (normalizedQ) {
    query.set('q', normalizedQ);
  }
  if (normalizedDoctorId) {
    query.set('doctorId', normalizedDoctorId);
  }

  const path = query.toString() ? `/api/calendar/calendars?${query.toString()}` : '/api/calendar/calendars';
  const payload = await apiRequest(path, {
    method: 'GET',
    headers: createAuthHeaders(token),
  });

  return {
    items: Array.isArray(payload?.items) ? payload.items : [],
    associations: payload?.associations || { doctors: [] },
    count: Number(payload?.count) || 0,
    raw: payload,
  };
}

export async function fetchCalendarData({
  token,
  scope = 'date',
  from,
  to,
  includeSlots = false,
  includeNotAvailableSlots = false,
  includeStandalone = false,
  doctorId,
  patientId,
  calendarIds,
} = {}) {
  const normalizedScope = toOptionalTrimmedString(scope) || 'date';
  if (!['date', 'doctor', 'patient'].includes(normalizedScope)) {
    throw new ApiError('scope must be one of: date, doctor, patient', 400);
  }

  const query = new URLSearchParams({
    scope: normalizedScope,
    from: toRfc3339(from, 'from'),
    to: toRfc3339(to, 'to'),
    ...(includeSlots ? { includeSlots: 'true' } : {}),
    ...(includeNotAvailableSlots ? { includeNotAvailableSlots: 'true' } : {}),
    // Doctor-less rows (standalone-calendar direct entries) are opt-in server-side.
    ...(includeStandalone ? { includeStandalone: 'true' } : {}),
  });

  const normalizedDoctorId = toOptionalTrimmedString(doctorId);
  const normalizedPatientId = toOptionalTrimmedString(patientId);
  if (normalizedDoctorId) {
    query.set('doctorId', normalizedDoctorId);
  }
  if (normalizedPatientId) {
    query.set('patientId', normalizedPatientId);
  }

  for (const calendarId of normalizeIdList(calendarIds)) {
    query.append('calendarId', calendarId);
  }

  const payload = await apiRequest(`/api/calendar/calendar?${query.toString()}`, {
    method: 'GET',
    headers: createAuthHeaders(token),
  });

  return {
    scope: String(payload?.scope || normalizedScope),
    from: String(payload?.from || ''),
    to: String(payload?.to || ''),
    items: Array.isArray(payload?.items) ? payload.items : [],
    associations: payload?.associations || { doctors: [], patients: [], calendars: [] },
    raw: payload,
  };
}

/**
 * GET /api/calendar/slots — only `start` is required; optional filters omit → all active calendars / doctors (prod: scope filters).
 * Tenant, doctor, and calendar ids accept repeated `tenantId` / `doctorId` / `calendarId` query keys (comma-separated strings are split client-side).
 * @example
 * await fetchCalendarSlots({ start: fromIso, end: toIso });
 * await fetchCalendarSlots({ tenantIds: [tid], start: fromIso, doctorIds: [did], calendarIds: [cid] });
 */
export async function fetchCalendarSlots({
  token,
  tenantId,
  tenantIds,
  start,
  from,
  end,
  to,
  doctorId,
  doctorIds,
  calendarId,
  calendarIds,
  includeManual,
  limit,
  skip,
} = {}) {
  const startIso = toRfc3339(from ?? start, 'start');
  const query = new URLSearchParams({ start: startIso });

  for (const id of normalizeTenantIds({ tenantId, tenantIds })) {
    query.append('tenantId', id);
  }

  const endIso = toOptionalRfc3339(end ?? to, 'end');
  if (endIso) {
    query.set('end', endIso);
  }

  for (const id of normalizeMultiValueIds(doctorIds, doctorId)) {
    query.append('doctorId', id);
  }

  for (const id of normalizeSlotCalendarIds({ calendarId, calendarIds })) {
    query.append('calendarId', id);
  }

  if (isIncludeManualFlag(includeManual)) {
    query.set('includeManual', 'true');
  }

  query.set('limit', String(clampLimit(limit)));
  query.set('skip', String(toNonNegativeInt(skip)));

  const payload = await apiRequest(`/api/calendar/slots?${query.toString()}`, {
    method: 'GET',
    headers: createAuthHeaders(token),
  });

  return {
    from: String(payload?.from || ''),
    to: String(payload?.to || ''),
    count: Number(payload?.count) || 0,
    items: Array.isArray(payload?.items) ? payload.items : [],
    raw: payload,
  };
}

/**
 * GET /api/admin/slots/definitions — raw slot definition documents: one row per
 * slot with its recurrence rule and metadata intact (no occurrence expansion, no
 * availability filtering). Admin employees only. `start` is required; `end` is
 * optional (defaults to start + 7d server-side). Tenant, doctor, and calendar ids
 * accept repeated or comma-separated values.
 * @example
 * await fetchSlotDefinitions({ start: fromIso, end: toIso });
 */
export async function fetchSlotDefinitions({
  token,
  tenantId,
  tenantIds,
  start,
  from,
  end,
  to,
  doctorId,
  doctorIds,
  calendarId,
  calendarIds,
  limit,
  skip,
} = {}) {
  const startIso = toRfc3339(from ?? start, 'start');
  const query = new URLSearchParams({ start: startIso });

  for (const id of normalizeTenantIds({ tenantId, tenantIds })) {
    query.append('tenantId', id);
  }

  const endIso = toOptionalRfc3339(end ?? to, 'end');
  if (endIso) {
    query.set('end', endIso);
  }

  for (const id of normalizeMultiValueIds(doctorIds, doctorId)) {
    query.append('doctorId', id);
  }

  for (const id of normalizeSlotCalendarIds({ calendarId, calendarIds })) {
    query.append('calendarId', id);
  }

  query.set('limit', String(clampLimit(limit)));
  query.set('skip', String(toNonNegativeInt(skip)));

  const payload = await apiRequest(`/api/admin/slots/definitions?${query.toString()}`, {
    method: 'GET',
    headers: createAuthHeaders(token),
  });

  return {
    from: String(payload?.from || ''),
    to: String(payload?.to || ''),
    count: Number(payload?.count) || 0,
    items: Array.isArray(payload?.items) ? payload.items : [],
    raw: payload,
  };
}
